<template>
  <div>
    <b-card>
      <div class="organism-site-access-management_header card-padding">
        <span class="header__label">Site Access Management</span>
        <div v-if="hasUpdateSiteAccessPermission">
          <atom-button
            buttonId="btn-create"
            class="header__button mt-1 mt-sm-0"
            buttonStyle="border"
            @click="onClickCreate"
            >Create New Site Access</atom-button
          >
        </div>
      </div>
      <!-- Filter Start -->
      <div class="filter-container card-padding pt-0">
        <div class="row mt-0">
          <div class="col-12 col-sm-6">
            <atom-multi-select
              v-model="filterList.visitors"
              title="Visitors"
              placeholder="Select Visitors"
              label="name"
              :option="visitorOptions"
            ></atom-multi-select>
          </div>
          <div class="col-12 col-sm-6 col-lg-3 mt-1 mt-sm-0">
            <atom-input
              v-model="filterList.siteAccessId"
              title="Site Access ID"
              placeholder="Site Access ID"
              @onKeyupEnter="onClickSearch"
            ></atom-input>
          </div>
          <div class="col-12 col-sm-6 col-lg-3 mt-1 mt-lg-0">
            <atom-input
              v-model="filterList.idcId"
              :title="contractIdLabel"
              :placeholder="contractIdLabel"
              @onKeyupEnter="onClickSearch"
            ></atom-input>
          </div>
          <div class="col-12 col-sm-6 col-lg-6 mt-1">
            <atom-input
              v-model="filterList.description"
              title="Description"
              placeholder="Description"
              @onKeyupEnter="onClickSearch"
            ></atom-input>
          </div>
          <div class="col-12 col-lg-6 mt-1">
            <atom-multi-select
              v-model="filterList.status"
              title="Status"
              placeholder="All Status"
              label="name"
              :option="statusOptions"
            ></atom-multi-select>
          </div>
        </div>

        <div class="row mt-1">
          <div class="col-12 col-sm-6 col-md-4 col-lg-3 w-100">
            <div>
              <atom-datepicker
                datepickerId="request__start_date"
                title="Start Date"
                v-model="filterList.requestDate.start"
                :maxDate="
                  filterList.requestDate.end
                    ? filterList.requestDate.end
                    : filterList.requestDate.now
                "
                type="dateNtime"
                placeholder="Select Request Start Date"
                :isDisabled="!filterList.isFilterDate"
              ></atom-datepicker>
            </div>
          </div>
          <div class="col-12 col-sm-6 col-md-4 col-lg-3 w-100 mt-1 mt-sm-0">
            <div>
              <atom-datepicker
                datepickerId="request__end_date"
                title="End Date"
                v-model="filterList.requestDate.end"
                :minDate="filterList.requestDate.start"
                type="dateNtime"
                placeholder="Select Request End Date"
                :isDisabled="!filterList.isFilterDate"
              ></atom-datepicker>
            </div>
          </div>
          <div
            class="col-12 col-md-4 col-lg-3 d-flex d-flex justify-content-end justify-content-md-start mt-1 mt-md-0"
          >
            <div class="d-flex flex-column">
              <label>Filter Date?</label>
              <atom-switch
                v-model="filterList.isFilterDate"
                class="filter__switch"
              ></atom-switch>
            </div>
            <div class="d-flex align-items-end">
              <atom-button
                buttonId="button-search"
                iconLeft="SearchIcon"
                @click="onClickSearch"
                class="search__button ml-2"
              ></atom-button>
              <div
                class="reset-filter-button ml-1"
                @click="clickButtonClearFilter"
              >
                <span>Reset</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-2">
        <atom-select
          v-model="filterList.orderBy"
          title="Order By"
          placeholder="Select Order By"
          label="name"
          :isAppendToBody="false"
          :isEnableProper="false"
          :option="orderByOptions"
          @change="onChangeOrderBy"
          class="col-12 col-lg-4 px-2"
        ></atom-select>
      </div>

      <div class="row mt-3">
        <div class="col-12 table-wrapper">
          <b-table
            sticky-header
            responsive
            :items="siteAccessList"
            :fields="siteAccessFields"
            :hover="true"
            @row-clicked="onClickRow"
            show-empty
          >
            <template #cell(id)="data">
              <div class="text__table-header">
                {{ data.value.siteAccessId }}
              </div>
              <div class="text__table-sub mt-4px">
                {{ `${contractIdLabel}: ${data.value.idcId}` }}
              </div>
              <b-badge
                pill
                :variant="badgeVariant(data.value.status)"
                class="badge--width mt-8px"
                >{{ badgeStatus(data.value.status) }}</b-badge
              >
            </template>
            <template #cell(requestStartDate)="data">
              <div class="text__table-date">{{ data.value.date }}</div>
              <div class="text__table-time">{{ data.value.time }}</div>
            </template>
            <template #cell(requestEndDate)="data">
              <div class="text__table-date">{{ data.value.date }}</div>
              <div class="text__table-time">{{ data.value.time }}</div>
            </template>
            <template #cell(checkInDate)="data">
              <div
                class="text__table-date"
                :class="{ 'pl-3': !data.value.date }"
              >
                {{ data.value.date || "-" }}
              </div>
              <div class="text__table-time">{{ data.value.time }}</div>
            </template>
            <template #cell(checkOutDate)="data">
              <div
                class="text__table-date"
                :class="{ 'pl-3': !data.value.date }"
              >
                {{ data.value.date || "-" }}
              </div>
              <div class="text__table-time">{{ data.value.time }}</div>
            </template>
            <template #cell(visitors)="data">
              <li
                v-for="(visitor, index) in data.value.slice(0, 3)"
                :key="index"
                class="text__table-visitor"
              >
                {{ visitor }}
              </li>
              <div v-if="data.value.length > 3">
                <span
                  :id="'visitor' + data.index"
                  class="text-primary font-size-12"
                  ><u>+ {{ data.value.length - 3 }} Visitors</u></span
                >

                <b-popover
                  ref="popover"
                  :target="'visitor' + data.index"
                  triggers="hover"
                  placement="right"
                  container="my-container"
                  boundary="window"
                >
                  <template #title>
                    <span class="font-size-14">All Visitors</span>
                  </template>
                  <div class="pl-1">
                    <li v-for="(visitor, index) in data.value" :key="index">
                      {{ visitor }}
                    </li>
                  </div>
                </b-popover>
              </div>
            </template>

            <template #cell(description)="data">
              <div class="text__table-description d-flex">
                <span class="custom-sub-max-width">
                  {{ data.value }}
                </span>
              </div>
            </template>

            <template #empty>
              <div
                class="d-flex flex-column align-items-center justify-content-center my-4"
              >
                <feather-icon
                  icon="CloudOffIcon"
                  class="empty-data__icon"
                ></feather-icon>
                <label class="empty-data__text--title">No Data</label>
              </div>
            </template>
          </b-table>
        </div>
      </div>
      <molecule-pagination-with-per-page
        :page="filterList.currentPage"
        :total-count="filterList.totalCount"
        :per-page="filterList.perPage"
        @onUpdatePaginationOrPerPage="onUpdatePaginationOrPerPage"
      ></molecule-pagination-with-per-page>
    </b-card>
  </div>
</template>

<script>
import mixins from "@/mixins"
import mixinsDateTime from "@/mixins/mixinsDatetime"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomMultiSelect from "@/components/atoms/AtomMultiSelect.vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomDatepicker from "@/components/atoms/AtomDatepicker.vue"
import AtomSwitch from "@/components/atoms/AtomSwitch.vue"
import AtomSelect from "@/components/atoms/AtomSelect.vue"
import { BCard, BTable, BBadge, BPagination, BPopover } from "bootstrap-vue"
import MoleculePaginationWithPerPage from "@/components/molecules/MoleculePaginationWithPerPage.vue"
import mixinsUser from "@/mixins/mixinsUser"
import { PermissionUser } from "@/constants/permission-user"

export default {
  name: "OrganismInternalQrAccessManagement",
  components: {
    AtomButton,
    AtomMultiSelect,
    AtomInput,
    AtomDatepicker,
    AtomSwitch,
    AtomSelect,
    MoleculePaginationWithPerPage,
    BCard,
    BTable,
    BBadge,
    BPagination,
    BPopover,
  },
  mixins: [mixins, mixinsDateTime, mixinsUser],
  props: {
    filterList: {
      required: true,
      type: Object,
    },
    visitorOptions: {
      required: true,
      type: Array,
    },
    siteAccessList: {
      required: true,
      type: Array,
    },
  },
  emits: [
    "onUpdateFilter",
    "onUpdateCurrentPageOrPerPage",
    "onUpdateOrderBy",
    "onSetDefaultFilterData",
    "onClickRow",
  ],
  data() {
    return {
      statusOptions: [
        { id: "WAITING", name: "Waiting" },
        { id: "APPROVED", name: "Approved" },
        { id: "REJECTED", name: "Rejected" },
        { id: "ACCESS", name: "Access" },
        { id: "CLOSED", name: "Closed" },
      ],

      orderByOptions: [
        {
          id: "Status",
          name: "Status Ascending",
        },
        {
          id: "Status desc",
          name: "Status Descending",
        },
        {
          id: "RequestStartDate",
          name: "Request Start Date Ascending",
        },
        {
          id: "RequestStartDate desc",
          name: "Request Start Date Descending",
        },
        {
          id: "RequestEndDate",
          name: "Request End Date Ascending",
        },
        {
          id: "RequestEndDate desc",
          name: "Request End Date Descending",
        },
        {
          id: "CheckInDate",
          name: "Check In Date Ascending",
        },
        {
          id: "CheckInDate desc",
          name: "Check In Date Descending",
        },
        {
          id: "CheckOutDate",
          name: "Check Out Date Ascending",
        },
        {
          id: "CheckOutDate desc",
          name: "Check Out Date Descending",
        },
      ],

      siteAccessFields: [
        {
          key: "id",
          label: "Site Access ID",
        },
        {
          key: "site",
          label: "Site",
        },
        {
          key: "requestStartDate",
          label: "Request Start Date",
        },
        {
          key: "requestEndDate",
          label: "Request End Date",
        },
        {
          key: "checkInDate",
          label: "Check In Date",
        },
        {
          key: "checkOutDate",
          label: "Check Out Date",
        },
        {
          key: "visitors",
          label: "Visitors",
        },
        {
          key: "description",
          label: "DESCRIPTION",
        },
      ],
    }
  },
  computed: {
    hasUpdateSiteAccessPermission() {
      return this.hasPermission(PermissionUser.WPM.SITE_ACCESS_WRITE)
    },
  },
  methods: {
    onClickCreate() {
      this.$router.push({ name: "internalCreateNewSiteAccess" })
    },

    onClickSearch() {
      this.$emit("onUpdateFilter", this.filterList)
    },

    onClickRow(row) {
      this.$router.push(
        `/internal/web-portal/site-access/${row.id.siteAccessId}`
      )
    },

    clickButtonClearFilter() {
      this.$emit("onSetDefaultFilterData")
    },

    badgeVariant(status) {
      if (status === "WAITING") return "light-warning"
      if (status === "APPROVED") return "light-success"
      if (status === "REJECTED") return "light-danger"
      if (status === "ACCESS") return "light-primary"
      return "light-secondary"
    },

    badgeStatus(status) {
      if (status === "WAITING") return "Waiting"
      if (status === "REJECTED") return "Rejected"
      if (status === "APPROVED") return "Approved"
      if (status === "ACCESS") return "Access"
      return "Closed"
    },

    onUpdatePaginationOrPerPage(currentPerPage, currentPage) {
      const updatedFilter = {
        ...this.filterList,
        currentPage,
        perPage: currentPerPage,
      }
      this.$emit("onUpdateCurrentPageOrPerPage", updatedFilter)
    },

    onChangeOrderBy(currentOrderBy) {
      const updatedFilter = {
        ...this.filterList,
        orderBy: currentOrderBy,
      }
      this.$emit("onUpdateOrderBy", updatedFilter)
    },
  },
}
</script>

<style lang="scss" scoped>
.table-wrapper ::v-deep.b-table-sticky-header {
  min-height: 525px;
  max-height: calc(100vh - 685px);
  &:has(.b-table-empty-row) {
    min-height: 350px;
  }
}

.organism-site-access-management_header {
  display: flex;
  flex-direction: column;
}

.header__label {
  font-weight: 500;
  font-size: 18px;
  color: #5e5873;
}

.card-body {
  padding: 0;
}

.card-padding {
  padding: 1.5rem;
}

.filter-container {
  border-bottom: 1px solid #e9ecef;
}

.filter__switch {
  padding-top: 10px;
  height: fit-content;
}

.search__button {
  margin-top: 23px;
  margin-right: 0;
  height: fit-content;
}

.reset-filter-button {
  color: #b9b9c3;
  margin-bottom: 8px;
  text-decoration: underline;
  &:hover {
    cursor: pointer;
    color: #7367f0;
  }
}

.mt-4px {
  margin-top: 4px;
}

.mt-8px {
  margin-top: 8px;
}

.text__table-header {
  color: #6e6b7b;
  font-size: 14px;
  font-weight: 600;
}

.text__table-sub,
.text__table-time {
  color: #b9b9c3;
  font-size: 12px;
  font-weight: 400;
  width: 150px;
}

.text__table-date,
.text__table-visitor,
.text__table-description {
  color: #6e6b7b;
  font-size: 14px;
  font-weight: 400;
  width: 150px;
}

.text__table-visitor {
  width: 200px;
}

.text__table-description {
  width: 250px;
}

.custom-sub-max-width {
  max-width: 230px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @supports (-webkit-line-clamp: 3) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }
}

@media screen and (min-width: 576px) {
  .organism-site-access-management_header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
